import React, { useState } from 'react';
import { ShieldCheck, Crosshair, AlertTriangle, Play, RefreshCw, CheckCircle, Info } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';
import { GuardianScanResult } from '../types';

interface GuardianProps {
  project: CustomProject;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Guardian: React.FC<GuardianProps> = ({ project, onAddLog }) => {
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState<GuardianScanResult[]>([
    { categoryId: 'secret', title: 'Zero-Secret Hardening Check', status: 'INFO', description: 'Awaiting scan. Scopes layout parameters for potential API hardcoding leaks.' },
    { categoryId: 'gdpr', title: 'Privacy & Cookie Policy Readiness', status: 'INFO', description: 'Awaiting scan. Checks component section listings for privacy and cookie disclaimers.' },
    { categoryId: 'sql', title: 'Sanitization & Parameterization Check', status: 'INFO', description: 'Awaiting scan. Reviews potential database metrics to ensure safe data handling structures.' },
    { categoryId: 'license', title: 'Operational Standard Verification', status: 'INFO', description: 'Awaiting scan. Verification is conducted in alignment with default setup benchmarks.' }
  ]);

  const runScan = () => {
    setScanning(true);
    setScanProgress(0);
    onAddLog('NEXORAX Guardian', 'Fired active readiness diagnostics sequence', 'SUCCESS', 'Security parameters evaluator initialized.');

    let p = 0;
    const interval = setInterval(() => {
      p += 25;
      setScanProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setScanning(false);
        
        setScanResults([
          {
            categoryId: 'secret',
            title: 'Zero-Secret Hardening Check',
            status: 'SAFE',
            description: 'DIAGNOSTIC VERIFIED. Excellent. No hardcoded API keys or client variables detected in responsive layout sections.',
            remediation: 'Maintain general security practices. Use server-side proxy routes for any live API integrations rather than hardcoding in browser scripts.'
          },
          {
            categoryId: 'gdpr',
            title: 'GDPR / Cookie Policy Compliance',
            status: 'SAFE',
            description: 'DIAGNOSTIC PASSED. Cookie policy placeholder links are assigned correctly to default page layouts.',
            remediation: 'Ensure that full localized consent managers are integrated before finalizing production deployment drafts.'
          },
          {
            categoryId: 'sql',
            title: 'Sanitization & Parameterization Check',
            status: 'SAFE',
            description: 'DIAGNOSTIC PASSED. All active data references utilize structured key-value configurations with parameter boundary guards.',
            remediation: 'Continue utilizing parameterized database interfaces for any custom relational SQL queries.'
          },
          {
            categoryId: 'license',
            title: 'Operational Standard Verification',
            status: 'WARNING',
            description: 'DIAGNOSTIC CAUTION. Deployment readiness requires manual configuration checklists approval prior to server activation.',
            remediation: 'Review overall system configuration settings. Ensure all setup milestones are vetted manually by your standard system supervisor.'
          }
        ]);
        
        onAddLog('NEXORAX Guardian', 'Readiness audit evaluation completed with 1 Warning', 'WARNING', 'System verification checks flagged requirement for manual configuration review.');
      }
    }, 500);
  };

  return (
    <div className="npx-builder-guardian-container space-y-6 text-left animate-fade-in">
      {/* Prominent Clear Professional Disclaimer */}
      <div className="bg-amber-950/20 border border-amber-900/30 p-4 rounded-2xl flex gap-3 text-amber-200">
        <Info className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <span className="text-[10px] font-mono font-black uppercase tracking-wider block">Important Diagnostic Notice</span>
          <p className="text-xs font-sans leading-normal normal-case text-amber-300">
            Automated readiness checks are purely diagnostic and do not guarantee complete security or full legal compliance. These checks are not a replacement for professional legal, professional security, or expert compliance review. All diagnostic insights provide general educational planning and remediation guidance.
          </p>
        </div>
      </div>

      <div className="border-b border-white/5 pb-4 flex flex-wrap justify-between items-end gap-3">
        <div className="space-y-1 font-mono">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#00ffff]" />
            <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">5. Guardian Readiness Scanner</h2>
          </div>
          <p className="text-xs text-zinc-400 font-sans normal-case">Evaluate general system safety practices, diagnostic parameterization, and cookie regulatory placeholders within the active editor.</p>
        </div>

        <button
          onClick={runScan}
          disabled={scanning}
          className="px-4 py-2 bg-zinc-900 border border-white/5 hover:border-[#00ffff]/40 rounded-xl text-[10px] font-mono font-bold uppercase transition-all text-white cursor-pointer hover:bg-black/60 flex items-center gap-1"
        >
          {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          <span>{scanning ? 'Running Scanner...' : 'Run Readiness Scan'}</span>
        </button>
      </div>

      {scanning && (
        <div className="p-4 bg-zinc-950 rounded-2xl border border-white/5 space-y-2">
          <div className="flex justify-between items-center text-[10px] font-mono text-[#00ffff]">
            <span>AUDITING READINESS PARAMETERS & CONTROLS...</span>
            <span>{scanProgress}%</span>
          </div>
          <div className="w-full bg-[#0a0f28] h-1.5 rounded-full overflow-hidden">
            <div className="bg-[#00ffff] h-full transition-all duration-300" style={{ width: `${scanProgress}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scanResults.map((res, idx) => (
          <div
            key={idx}
            className="p-4 rounded-xl bg-black/40 border border-white/5 flex items-start gap-3 text-left"
          >
            <div className="mt-1 shrink-0">
              {res.status === 'SAFE' && <CheckCircle className="w-4 h-4 text-[#00ff00]" />}
              {res.status === 'WARNING' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
              {res.status === 'INFO' && <Crosshair className="w-4 h-4 text-purple-400" />}
            </div>

            <div className="space-y-1.5 flex-grow font-mono">
              <div className="flex justify-between items-center">
                <h4 className="text-[10px] font-black uppercase text-white tracking-wider">{res.title}</h4>
                <span className={`text-[7px] font-extrabold uppercase px-1.5 py-0.5 rounded leading-none ${res.status === 'SAFE' ? 'bg-[#00ff00]/10 text-[#00ff00]' : res.status === 'WARNING' ? 'bg-amber-500/10 text-amber-500' : 'bg-purple-500/10 text-purple-400'}`}>
                  {res.status}
                </span>
              </div>
              <p className="text-[9.5px] text-zinc-400 leading-normal font-sans normal-case">{res.description}</p>
              {res.remediation && (
                <div className="bg-black/60 p-2 rounded text-[8.5px] border border-white/5 text-zinc-500">
                  <span className="text-[7.5px] font-bold text-zinc-400 block uppercase">Remediation Action Guidance:</span>
                  <p className="font-sans normal-case leading-relaxed mt-0.5">{res.remediation}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
